import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatPrice } from "./Helpers";
import { FALLBACK_IMAGE, ENDPOINTS } from "./Constants";
import PropertyDistanceBadge from "./PropertyDistanceBadge";
import { useFavorites } from "../../features/favorites/FavoritesContext";
import { sessionStorageService } from "../../features/authentication";
import "../../styles/ai/ai-property-card.css";

// Best-effort, fire-and-forget -- never awaited by a caller, never blocks
// navigation or the dismiss action, and a failed send is silently dropped
// (mirrors every other best-effort write in this codebase, e.g.
// mailer.py's own contract). Only called at all when `source` is set,
// i.e. only from a Personal Agent surface -- see `engagementSource` below.
function trackEngagement(propertyId, eventType, source) {
  const token = sessionStorageService.get();
  if (!token || !propertyId || !source) return;
  fetch(ENDPOINTS.PERSONAL_AGENT_ENGAGEMENT, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ propertyId, eventType, source }),
  }).catch(() => { /* best-effort -- never surfaced to the user */ });
}

/**
 * Property result card shown inside the AI chat — displays pricing, specs,
 * AI match score/reasons, and image gallery, and wires up watchlist,
 * compare, map, and contact actions for a single matched property.
 *
 * `engagementSource` is set only by Personal Agent surfaces (the Recents
 * dashboard, the guided-search chat) -- when present, opening or dismissing
 * the card records a `viewed`/`dismissed` event; absent (every other AI
 * surface, e.g. the free /ai-search chat) neither ever fires, so this stays
 * scoped to Personal Agent rather than becoming a site-wide view tracker.
 */
const PRIORITY_LABELS = { FIXED: "Must-have", HIGH: "High", MEDIUM: "Medium", LOW: "Low" };
const PRIORITY_ORDER  = { FIXED: -1, HIGH: 0, MEDIUM: 1, LOW: 2 };

function AiPropertyCard({
                          property,
                          onMapView,
                          isMapOpen,
                          onRemove,
                          userPosition,
                          isInCompare = false,
                          onCompare,
                          engagementSource = null,
                          onDismiss,
                        }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFavorited, toggleFavorite } = useFavorites();
  const [currentImg, setCurrentImg] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const watchlisted = property ? isFavorited(property.id) : false;

  // All hooks must run on every render regardless of `property` being present
  // (React requires the same hooks in the same order every time), so the
  // `!property` bail-out has to come after every useMemo below, not before.
  const bhkDisplay = useMemo(() => {
    if (!property) return null;
    if (property.bhk) {
      const s = String(property.bhk).trim();
      return /bhk/i.test(s) ? s : `${s} BHK`;
    }
    if (property.bedrooms && property.bedrooms > 0) return `${property.bedrooms} BHK`;
    return null;
  }, [property]);

  const displayPrice = useMemo(() => {
    if (!property?.price) return "Price on request";
    const s = String(property.price);
    if (s.includes("₹") || /\bCr\b|\bL\b/i.test(s)) return s;
    return formatPrice(property.priceRaw ?? property.price);
  }, [property]);

  // Only a real score gets a badge -- never one made up from the property id (FINAL plan P17).
  // Priority-matched cards have no percentage at all: their position is explained by the priority summary.
  const aiMatch = useMemo(() => {
    if (!property || property.rankingBasis === "priority") return null;
    if (property.aiMatchScore != null && property.aiMatchScore > 0) {
      return Math.min(99, Math.max(10, property.aiMatchScore));
    }
    return null;
  }, [property]);

  const matchReasons = useMemo(() => {
    if (!Array.isArray(property?.matchReasons)) return [];
    return property.matchReasons.filter((r) => typeof r === "string" && r.trim().length > 0);
  }, [property]);

  const compromises = useMemo(() => {
    if (!Array.isArray(property?.compromises)) return [];
    return property.compromises.filter((r) => typeof r === "string" && r.trim().length > 0);
  }, [property]);

  // Priority matching (backend matching/): every requirement checked as Meets / Doesn't meet / Not verified.
  const requirementResults = useMemo(
      () => (Array.isArray(property?.requirementResults) ? property.requirementResults : []),
      [property]
  );
  const priorityRanked = property?.rankingBasis === "priority" && requirementResults.length > 0;
  const prioritised = useMemo(
      () => requirementResults
          // Must-haves are summed up as "Meets all N"; one measured from the map ("within 15 km of …")
          // also shows its distance.
          .filter((r) => r.priority !== "FIXED" || r.attribution)
          .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9)),
      [requirementResults]
  );
  const mustHaveCount = requirementResults.filter((r) => r.priority === "FIXED").length;
  const prioritySummary = useMemo(() => {
    const match = property?.priorityMatch;
    if (!match) return "";
    return ["high", "medium", "low"]
        .filter((level) => match[level]?.total > 0)
        .map((level) => `${level[0].toUpperCase()}${level.slice(1)} ${match[level].pass}/${match[level].total}`)
        .join(" · ");
  }, [property]);

  if (!property) return null;

  const images  = property.gallery?.length > 0
      ? property.gallery
      : [property.primaryImage || property.image || FALLBACK_IMAGE];
  const soldOut = Boolean(property.soldOut);

  const nextImg = (e) => { e.stopPropagation(); setCurrentImg((p) => (p + 1) % images.length); };
  const prevImg = (e) => { e.stopPropagation(); setCurrentImg((p) => (p - 1 + images.length) % images.length); };

  const handleClick         = () => {
    trackEngagement(property.id, "viewed", engagementSource);
    navigate(`/property/${property.slug || property.id}`);
  };
  const handleWatchlist     = (e) => {
    e.stopPropagation();
    if (!sessionStorageService.get()) {
      navigate("/auth", { state: { redirectTo: location.pathname } });
      return;
    }
    toggleFavorite(property.id);
  };
  const handleLocationClick = (e) => { e.stopPropagation(); onMapView?.(); };
  const handleCompare       = (e) => { e.stopPropagation(); onCompare?.(property); };
  const handleDismiss       = (e) => {
    e.stopPropagation();
    trackEngagement(property.id, "dismissed", engagementSource);
    setDismissed(true);
    onDismiss?.(property.id);
  };

  return (
      <div className={`pcard${isInCompare ? " pcard--comparing" : ""}`}>

        <div className="pcard-img" onClick={handleClick}>
          <img src={images[currentImg]} alt={property.title || "Property"}
               loading="lazy" onError={(e) => { e.target.src = FALLBACK_IMAGE; }} />
          {images.length > 1 && (
              <div className="pcard-counter">🖼 {currentImg + 1}/{images.length}</div>
          )}
          {images.length > 1 && (
              <>
                <button className="pcard-arrow l" onClick={prevImg} aria-label="Previous">‹</button>
                <button className="pcard-arrow r" onClick={nextImg} aria-label="Next">›</button>
              </>
          )}
          <div className="pcard-price">{displayPrice}</div>
          <div className="pcard-tags">
            {property.reraApproved && (
                <span className="ptag rera">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  RERA
                </span>
            )}
            {property.vastuCompliant && <span className="ptag vastu">Vastu ✓</span>}
            {soldOut && <span className="ptag sold">SOLD OUT</span>}
          </div>
          <button className={`pcard-fav${watchlisted ? " pcard-fav--saved" : ""}`}
                  onClick={handleWatchlist}
                  aria-label={watchlisted ? "Remove from wishlist" : "Add to wishlist"}>
            <svg width="16" height="16" viewBox="0 0 24 24"
                 fill={watchlisted ? "#ef4444" : "none"}
                 stroke={watchlisted ? "#ef4444" : "#64748b"}
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
        </div>

        <div className="pcard-body">

          {onRemove && (
              <button className="pcard-x"
                      onClick={(e) => { e.stopPropagation(); onRemove(property.id); }}
                      aria-label="Remove">✕</button>
          )}

          <div className="pcard-title-row">
            <h3 className="pcard-title" onClick={handleClick}>
              {property.title || "Untitled Property"}
            </h3>
            {aiMatch != null && (
                <div className="pcard-ai-match" aria-label={`${aiMatch}% AI Match`}>
                  <div className="pcard-ai-circle">{aiMatch}%</div>
                  <span className="pcard-ai-label">AI Match</span>
                </div>
            )}
          </div>

          <div className="pcard-loc pcard-loc-clickable"
               onClick={handleLocationClick} title="View on map"
               role="button" tabIndex={0}
               onKeyDown={(e) => e.key === "Enter" && handleLocationClick(e)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
              <circle cx="12" cy="10" r="3"/>
              <path d="M12 2a8 8 0 0 1 8 8c0 5.25-8 12-8 12S4 15.25 4 10a8 8 0 0 1 8-8z"/>
            </svg>
            <span className="pcard-loc-text">{property.location || "Location not specified"}</span>

            {property.distanceLabel ? (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "2px 8px", borderRadius: 20,
                  fontSize: 12, fontWeight: 600,
                  background: "#dbeafe", color: "#1e40af",
                  whiteSpace: "nowrap",
                }}>
                    📍 {property.distanceLabel}
                </span>
            ) : (
                <PropertyDistanceBadge
                    userLat={userPosition?.latitude}
                    userLng={userPosition?.longitude}
                    propLat={property.latitude ?? property.lat}
                    propLng={property.longitude ?? property.lng ?? property.lon}
                />
            )}
          </div>

          <div className="pcard-specs">
            {bhkDisplay && (
                <span className="pcard-chip pcard-chip-spec">
                  <span className="pcard-spec-icon pcard-spec-icon--bed" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 9V19H22V9"/><path d="M2 9C2 9 2 5 12 5C22 5 22 9 22 9"/>
                      <path d="M12 5V9"/><rect x="5" y="11" width="4" height="3" rx="1"/>
                      <rect x="15" y="11" width="4" height="3" rx="1"/>
                    </svg>
                  </span>
                  <span className="pcard-spec-value">{bhkDisplay}</span>
                </span>
            )}
            {property.bathrooms > 0 && (
                <span className="pcard-chip pcard-chip-spec">
                  <span className="pcard-spec-icon pcard-spec-icon--bath" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 6C9 4.34 7.66 3 6 3C4.34 3 3 4.34 3 6L3 12"/>
                      <path d="M3 12L21 12L21 14C21 17.31 18.31 20 15 20L9 20C5.69 20 3 17.31 3 14Z"/>
                      <line x1="9" y1="20" x2="9" y2="22"/><line x1="15" y1="20" x2="15" y2="22"/>
                    </svg>
                  </span>
                  <span className="pcard-spec-value">{property.bathrooms}</span>
                  <span className="pcard-spec-label">Baths</span>
                </span>
            )}
            {property.sqft > 0 && (
                <span className="pcard-chip pcard-chip-spec">
                  <span className="pcard-spec-icon pcard-spec-icon--area" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <path d="M3 9L9 3M3 15L15 3"/>
                    </svg>
                  </span>
                  <span className="pcard-spec-value">{Number(property.sqft).toLocaleString("en-IN")}</span>
                  <span className="pcard-spec-label">sqft</span>
                </span>
            )}
          </div>

          {priorityRanked && (
              <div className="pcard-reqs" aria-label="How this property matches your requirements">
                {mustHaveCount > 0 && (
                    <div className="pcard-reqs-musthaves">
                      ✓ Meets all {mustHaveCount} must-have{mustHaveCount === 1 ? "" : "s"}
                    </div>
                )}
                {prioritySummary && (
                    <div className="pcard-reqs-summary">
                      <span className="pcard-reqs-summary-label">Priority match:</span> {prioritySummary}
                    </div>
                )}
                {[
                  { statuses: ["PASS"], title: "Why this fits you", icon: "✓", tone: "pass" },
                  { statuses: ["PRESENT"], title: "Distances", icon: "•", tone: "metric" },
                  { statuses: ["FAIL"], title: "Compromises", icon: "✗", tone: "fail" },
                  // A distance we couldn't measure is "not verified", never "doesn't meet".
                  { statuses: ["UNKNOWN", "UNAVAILABLE"], title: "Not verified", icon: "?", tone: "unknown" },
                ].map(({ statuses, title, icon, tone }) => {
                  const status = statuses[0];
                  const rows = prioritised.filter((r) => statuses.includes(r.status));
                  if (rows.length === 0) return null;
                  return (
                      <div key={status} className={`pcard-reqs-group pcard-reqs-group-${tone}`}>
                        <div className="pcard-reqs-label">{title}</div>
                        <ul className="pcard-reqs-list">
                          {rows.map((r) => (
                              <React.Fragment key={r.requirementId}>
                                <li className={`pcard-req pcard-req-${tone}`}>
                                  <span className="pcard-req-icon" aria-hidden="true">{icon}</span>
                                  <span className="pcard-req-text">
                                    <span className="pcard-req-name">{r.label} ({PRIORITY_LABELS[r.priority] || r.priority})</span>
                                    <span className="pcard-req-display">{r.display}</span>
                                  </span>
                                </li>
                                {r.extraDisplay && (
                                    <li className={`pcard-req pcard-req-${tone}`}>
                                      <span className="pcard-req-icon" aria-hidden="true">{icon}</span>
                                      <span className="pcard-req-text">
                                        <span className="pcard-req-name">{r.extraLabel}</span>
                                        <span className="pcard-req-display">{r.extraDisplay}</span>
                                      </span>
                                    </li>
                                )}
                              </React.Fragment>
                          ))}
                        </ul>
                      </div>
                  );
                })}
              </div>
          )}

          {!priorityRanked && matchReasons.length > 0 && (
              <div className="pcard-match-reasons" role="list" aria-label="Why this property matches you">
                <div className="pcard-match-reasons-label">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                       strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Why this fits you
                </div>
                <ul className="pcard-match-reasons-list">
                  {matchReasons.map((reason, i) => (
                      <li key={i}>{reason}</li>
                  ))}
                </ul>
              </div>
          )}

          {!priorityRanked && compromises.length > 0 && (
              <div className="pcard-compromises" role="list" aria-label="What this property does not satisfy">
                <div className="pcard-compromises-label">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                       strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L14.71 3.86a2 2 0 0 0-3.42 0z"/>
                  </svg>
                  Compromises
                </div>
                <ul className="pcard-compromises-list">
                  {compromises.map((reason, i) => (
                      <li key={i}>{reason}</li>
                  ))}
                </ul>
              </div>
          )}

          <div className="pcard-acts">
            <button className="pact pact-interest" onClick={handleClick}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              Show Interest
            </button>

            <button className="pact pact-call"
                    onClick={(e) => { e.stopPropagation(); window.open(`tel:${property.contactPhone || "+919494808825"}`); }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
                <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
              </svg>
              Call
            </button>

            <button className="pact pact-tour" onClick={handleClick}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <polygon points="23 7 16 12 23 17 23 7"/>
                <rect x="1" y="5" width="15" height="14" rx="2"/>
              </svg>
              Live Video Tour
            </button>

            <button className={`pact pact-mapview${isMapOpen ? " active" : ""}`}
                    onClick={(e) => { e.stopPropagation(); onMapView?.(); }}
                    aria-label="View on map">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                   strokeLinejoin="round" aria-hidden="true">
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
              </svg>
              View on Map
            </button>

            {onCompare && (
                <button
                    className={`pact pact-compare${isInCompare ? " pact-compare--active" : ""}`}
                    onClick={handleCompare}
                    aria-label={isInCompare ? "Remove from compare" : "Add to compare"}
                    aria-pressed={isInCompare}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                       aria-hidden="true">
                    {isInCompare
                        ? <polyline points="20 6 9 17 4 12"/>
                        : <><line x1="18" y1="20" x2="18" y2="10"/>
                          <line x1="12" y1="20" x2="12" y2="4"/>
                          <line x1="6"  y1="20" x2="6"  y2="14"/></>
                    }
                  </svg>
                  {isInCompare ? "✓ Comparing" : "Compare"}
                </button>
            )}

            <button className={`pact pact-watchlist-btn${watchlisted ? " watchlisted" : ""}`}
                    onClick={handleWatchlist}
                    aria-label={watchlisted ? "Remove from watchlist" : "Add to watchlist"}>
              <svg width="12" height="12" viewBox="0 0 24 24"
                   fill={watchlisted ? "currentColor" : "none"}
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                   strokeLinejoin="round" aria-hidden="true">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              {watchlisted ? "Saved ♥" : "Watchlist"}
            </button>

            {engagementSource && (
                <button className="pact pact-dismiss"
                        onClick={handleDismiss}
                        disabled={dismissed}
                        aria-label="Not interested in this property">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  {dismissed ? "Noted" : "Not interested"}
                </button>
            )}
          </div>

        </div>
      </div>
  );
}

export default React.memo(AiPropertyCard);